import { useState, ReactNode } from "react";
import { TaggedNoteEvent, CaseEvent, InformationRequest } from "./CaseDetailDrawer";
import { Case } from "./CaseTable";
import styles from "./caseStatus.module.css";
import { Alert, Dialog, Button } from "@narmi/design_system";
import { dateOptions } from "./PaymentDetailDrawer";
import { formatInformationRequest } from "./FormatInformationRequest";
import {
  CANCELLATION_CODES,
  ACWP_REASONS,
  RETURN_ACCEPT_REASONS,
  RETURN_REJECT_REASONS,
  CASE_RESOLUTIONS,
  ACWP_REASONS_TYPE,
  CANCELLATION_CODES_TYPE,
  CASE_RESOLUTIONS_TYPE,
  RETURN_ACCEPT_REASONS_TYPE,
  RETURN_REJECT_REASONS_TYPE,
} from "./codesets";

const alertIcons = {
  warn: "alert-triangle",
  success: "check",
  info: "alert-circle",
  error: "alert-circle",
};

export default function CaseStatusAlert({
  caseobj,
  history,
  informationRequests,
}: {
  history: Array<TaggedNoteEvent>;
  caseobj: Case;
  informationRequests: Map<string, InformationRequest>;
}) {
  const [dialogOpen, setDialogOpen] = useState(false);

  const first_event: CaseEvent | undefined = history.find(
    (ne) =>
      ne.type === "event" &&
      ((ne.content as CaseEvent).type === "created" ||
        (ne.content as CaseEvent).type === "ir_created")
  )?.content as CaseEvent;
  const acwpReasonCode =
    first_event && caseobj.kind === "ACWP" ? first_event.metadata.reason : null;
  const closedEvent: TaggedNoteEvent | undefined = history.find(
    (ne: TaggedNoteEvent) => ne.type === "event" && (ne.content as CaseEvent).type === "resolved"
  );

  let content = "";
  let extraInfo: string | ReactNode | undefined;
  let alertKind: "info" | "warn" | "error" | "success" | null = null;

  let dateElem: ReactNode | null = null;
  if (closedEvent !== undefined) {
    dateElem = (
      <span className={styles.noTruncate}>
        Closed on {new Date(closedEvent.content.created_at).toLocaleString("en-US", dateOptions)}
      </span>
    );
  } else if (first_event !== undefined && first_event.type === "ir_created") {
    dateElem = (
      <span className={styles.noTruncate}>
        Sent on {new Date(first_event.created_at).toLocaleString("en-US", dateOptions)}
      </span>
    );
  } else if (caseobj.deadline) {
    dateElem = (
      <span className={styles.noTruncate}>
        Due on {new Date(caseobj.deadline).toLocaleString("en-US", dateOptions)}
      </span>
    );
  }

  let dialogTitle = "Additional information";

  if (first_event !== undefined && first_event.type === "ir_created") {
    alertKind = "warn";
    const informationRequest = informationRequests.get(
      first_event.metadata.information_request_identification!
    );
    content = "Information requested.";
    dialogTitle = "Information request";
    extraInfo =
      informationRequest !== undefined ? formatInformationRequest(informationRequest) : null;
  } else if (caseobj.kind === "ACWP" && acwpReasonCode) {
    content = ACWP_REASONS[acwpReasonCode as ACWP_REASONS_TYPE] + ` [${acwpReasonCode}]`;
  } else if (caseobj.kind === "return") {
    if (caseobj.status === "open") {
      content =
        CANCELLATION_CODES[caseobj.initiation_reason as CANCELLATION_CODES_TYPE] +
        ` [${caseobj.initiation_reason}]`;
      extraInfo = (
        <>
          <p>{first_event?.metadata?.additional_info}</p>
        </>
      );
      alertKind = caseobj.deadline ? "error" : "info";
    } else {
      content = caseobj.resolution
        ? `Return request ${CASE_RESOLUTIONS[caseobj.resolution as CASE_RESOLUTIONS_TYPE]}.`
        : "Case closed.";
      if (closedEvent && (caseobj.resolution === "ipay" || caseobj.resolution === "pecr")) {
        extraInfo =
          "Accept reason: " +
          RETURN_ACCEPT_REASONS[
            (closedEvent.content as CaseEvent).metadata
              .resolution_reason as RETURN_ACCEPT_REASONS_TYPE
          ];
      } else if (closedEvent) {
        extraInfo = (
          <>
            <p>
              {"Reject reason: " +
                RETURN_REJECT_REASONS[
                  (closedEvent.content as CaseEvent).metadata
                    .reject_reason as RETURN_REJECT_REASONS_TYPE
                ]}
            </p>
            <p>{(closedEvent.content as CaseEvent).metadata?.additional_info}</p>
          </>
        );
      }
    }
  }

  alertKind = alertKind ? alertKind : caseobj.status === "open" ? "error" : "info";

  return (
    <div className="padding--top">
      <Alert isActive isDismissable={false} kind={alertKind} icon={alertIcons[alertKind]}>
        <div className={styles.truncateContainer}>
          <span className={`${styles.truncate} margin--right`} onClick={() => setDialogOpen(true)}>
            {content}
          </span>
          {dateElem !== null && dateElem}
        </div>
        {extraInfo && (
          <div className="padding--top--s">
            <Button
              kind="plain"
              style={{ color: "var(--color-grey)" }}
              label={`View ${dialogTitle.toLowerCase()}`}
              onClick={() => setDialogOpen(true)}
            />
          </div>
        )}
      </Alert>
      <Dialog onUserDismiss={() => setDialogOpen(false)} title={dialogTitle} isOpen={dialogOpen}>
        {content}
        <br />
        {extraInfo}
      </Dialog>
    </div>
  );
}
